import { useEffect, useMemo } from "react"
import { Empty, Segmented, Spin } from "antd"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faBus, faSatelliteDish, faArrowUpRightFromSquare, faChevronRight } from "@fortawesome/free-solid-svg-icons"
import PageHeader from "../../components/PageHeader"
import AnalyticsHelper from "../../helpers/analytics"
import config from "../../config"
import "../../components/Filter/Filter.less"
import "./ShuttlePage.less"

const TABS = [
    { label: 'Students and Staff', value: 'students-and-staff' },
    { label: 'Students Only', value: 'students-only' }
]

const slugToAudience = {
    'students-and-staff': 'students_and_staff',
    'students-only': 'students_only'
}

// The tracker publishes one fleet per audience: 科大職員巴士 vs 科大學生巴士.
const slugToGpsType = {
    'students-and-staff': 'staff',
    'students-only': 'student'
}

// `tel:` links have to stay in this tab so the dialler can pick them up.
const isExternal = (url) => /^https?:/i.test(url)

// A piece of copy from the API that optionally carries a link.
const InfoLink = ({ item, tab }) => {
    if (!item.url) return item.text

    const external = isExternal(item.url)

    return (
        <a
            className="shuttle-link"
            href={item.url}
            onClick={() => AnalyticsHelper.track('open_shuttle_link', { section: 'shuttle_bus', tab, link: item.text })}
            {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
        >
            {item.text}
            {external ? <FontAwesomeIcon icon={faArrowUpRightFromSquare} className="shuttle-link-icon" /> : null}
        </a>
    )
}

const ShuttlePage = (props) => {
    const { history, match, pageProps, getShuttleSchedules } = props
    const { shuttle_schedules, loading_shuttle_schedules } = pageProps

    const tab = match?.params?.tab === 'students-only' ? 'students-only' : 'students-and-staff'
    const audience = slugToAudience[tab]

    useEffect(() => {
        getShuttleSchedules()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const handleTabChange = (value) => {
        AnalyticsHelper.track('select_tab', { section: 'shuttle_bus', tab: value })
        history.push(`/shuttle-bus/${value}`)
    }

    const handleReload = () => {
        AnalyticsHelper.track('reload_eta', { section: 'shuttle_bus', tab })
        getShuttleSchedules()
    }

    const schedule = useMemo(
        () => (shuttle_schedules || []).find(s => s.audience === audience),
        [shuttle_schedules, audience]
    )

    return (
        <div className="container">
            <PageHeader icon={<FontAwesomeIcon icon={faBus} />} title="Shuttle Bus" onReload={handleReload} />

            <div className="filter">
                <Segmented
                    value={tab}
                    onChange={handleTabChange}
                    options={TABS}
                    className="filter-category"
                    block
                />
            </div>

            <div className="shuttle-content">
                {loading_shuttle_schedules && !schedule ? (
                    <Spin style={{ display: 'block', margin: '24px auto' }} />
                ) : schedule ? (
                    <>
                        <h2 className="shuttle-heading">{schedule.heading}</h2>
                        {schedule.period ? <p className="shuttle-meta">{schedule.period}</p> : null}
                        {schedule.service_note ? <p className="shuttle-meta">{schedule.service_note}</p> : null}

                        <a
                            className="shuttle-gps"
                            href={`${config.gpsTrackerUrl}${slugToGpsType[tab]}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() => AnalyticsHelper.track('open_gps_tracker', { section: 'shuttle_bus', tab })}
                        >
                            <FontAwesomeIcon icon={faSatelliteDish} className="shuttle-gps-icon" />
                            <span>GPS Information of buses</span>
                            <FontAwesomeIcon icon={faChevronRight} className="shuttle-gps-chevron" />
                        </a>

                        {(schedule.sections || []).map((section, i) => (
                            <div className="shuttle-section" key={i}>
                                <h3 className="shuttle-section-title">{section.title}</h3>
                                {section.note ? <p className="shuttle-meta">{section.note}</p> : null}
                                {(section.links || []).map((link, k) => (
                                    <p className="shuttle-meta" key={k}><InfoLink item={link} tab={tab} /></p>
                                ))}
                                {(section.entries || []).map((entry, j) => (
                                    <div
                                        className={`shuttle-entry${(entry.lines || []).length ? '' : ' shuttle-entry-compact'}`}
                                        key={j}
                                    >
                                        {entry.label ? <p className="shuttle-time">{entry.label}</p> : null}
                                        {(entry.lines || []).length ? (
                                            <ul className="shuttle-lines">
                                                {entry.lines.map((line, k) => <li key={k}>{line}</li>)}
                                            </ul>
                                        ) : null}
                                    </div>
                                ))}
                            </div>
                        ))}

                        {(schedule.other_information || []).length ? (
                            <div className="shuttle-section">
                                <h3 className="shuttle-section-title">Other Information</h3>
                                <ul className="shuttle-lines">
                                    {schedule.other_information.map((item, i) => (
                                        <li key={i}><InfoLink item={item} tab={tab} /></li>
                                    ))}
                                </ul>
                            </div>
                        ) : null}
                    </>
                ) : (
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No schedule" />
                )}
            </div>
        </div>
    )
}

export default ShuttlePage
